import { useCallback, useMemo } from 'react';

// eslint-disable-next-line lumapps/do-not-import-react-query
import { QueryFunctionContext, QueryKey, UseQueryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import merge from 'lodash/merge';

import { BaseApiError } from '@lumapps/base-api/types';
import { useUpdatedRef } from '@lumapps/lumx-hooks/hooks/useUpdatedRef';

import { StreamEvent, StreamingResponse } from '../types';
import { readStreamGenerator } from '../utils';

/**
 * Options to pass to the useStreamingQuery hook.
 * The generics allows you to determine which events will be received from the stream
 */
export type UseStreamingQueryOptions<
    /** The events that will be streamed by this query */
    Events extends StreamEvent,
    TQueryKey extends QueryKey = QueryKey,
    TError extends BaseApiError = BaseApiError,
> = Omit<UseQueryOptions<StreamingResponse<Events>, TError, StreamingResponse<Events>, TQueryKey>, 'queryFn'> & {
    /**
     * Function to be called to retrieve the stream.
     * The function must use the `stream` method from the BaseApi.
     */
    queryFn: (context: QueryFunctionContext<TQueryKey, unknown>) => Promise<AsyncGenerator<StreamingResponse<Events>>>;
    /**
     * Function to be called on error during the stream.
     * e.g. API GW closes the connection.
     */
    onReadError?: (error: unknown) => void;
};

/**
 * Hook to stream data using react query.
 * The hook overlays `useQuery` and allows to progressively fill the query key cache with incoming data.
 * To do so, a `onChunkDownload` function is sent to the queryFn that should use is to send incoming data.
 */
export const useStreamingQuery = <
    /** The events that will be streamed by this query */
    Events extends StreamEvent,
    TQueryKey extends QueryKey = QueryKey,
    TError extends BaseApiError = BaseApiError,
>(
    options: UseStreamingQueryOptions<Events, TQueryKey, TError>,
) => {
    const { queryFn, ...otherOptions } = options;
    const queryClient = useQueryClient();

    /**
     * `useUpdatedRef` stabilizes `queryKey` reference. Avoids `handleStopQuery` recreation and
     * subsequent `useEffect` re-runs in consuming hooks (e.g., useControlledStreamingQuery).
     */
    const queryKeyRef = useUpdatedRef(options.queryKey);

    const query = useQuery({
        // Streaming won't use cache in most of use cases. So we chose to default it to 0.
        cacheTime: options?.cacheTime || 0,
        staleTime: options?.staleTime || Infinity,
        queryFn: async (queryFnProps) => {
            const { queryKey } = queryFnProps;

            let response: StreamingResponse<Events> = {};

            /** Retrieve the async generator */
            const stream = await queryFn(queryFnProps);
            await readStreamGenerator(
                stream,
                (chunks) => {
                    response = merge({}, chunks);

                    /**
                     * Replace the current query data with the new chunks.
                     * We create a new object here to insure a new instance is created.
                     */
                    queryClient.setQueryData(queryKey, response);
                },
                (error) => {
                    options.onReadError?.(error);
                },
            );
            return response;
        },
        ...otherOptions,
    });

    const handleStopQuery = useCallback(() => {
        const nonNullQueryKeyRef = queryKeyRef.current ?? [];
        /**
         * React Query removes cached data when a query is manually cancelled.
         * To preserve the data when manually cancelling the streaming, we first store the current result,
         * cancel the query, then immediately restore the previous data.
         */
        const currentQueryData = queryClient.getQueryData(nonNullQueryKeyRef);
        queryClient.cancelQueries({ queryKey: nonNullQueryKeyRef });
        queryClient.setQueryData(nonNullQueryKeyRef, currentQueryData);

        // Cancel the reader to stop background streaming
        query.data?.reader?.cancel();
    }, [query.data?.reader, queryClient, queryKeyRef]);

    return useMemo(
        () => ({
            ...query,
            isLoading: query?.isFetching && query?.isLoading,
            isStreaming: !!query.data && query?.isFetching,
            stopStream: handleStopQuery,
        }),
        [query, handleStopQuery],
    );
};
